import {
  BicycleFill24Icon,
  BicycleOutline24Icon,
  CafeFill24Icon,
  CafeOutline24Icon,
  FamilyFill24Icon,
  FamilyOutline24Icon,
  PetAllowedFill24Icon,
  PetAllowedOutline24Icon,
  PetNotAllowedFill24Icon,
  PetNotAllowedOutline24Icon,
  PlussSeatFill24Icon,
  PlussSeatOutline24Icon,
  RecliningSeatFill24Icon,
  RecliningSeatOutline24Icon,
  Sleep6BedsFill24Icon,
  Sleep6BedsOutline24Icon,
  SleepFill24Icon,
  SleepOutline24Icon,
  SofaFill24Icon,
  SofaOutline24Icon,
  SoundOffFill24Icon,
  SoundOffOutline24Icon,
  ToiletFill24Icon,
  ToiletOutline24Icon,
  WifiFill24Icon,
  WifiOutline24Icon,
} from "@vygruppen/spor-icon-react";
import { CommonTrainInfoFormProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoForm";
import { CommonSubTypeProps } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/TrainInfoFormModal";
import { AffectedStopsFields } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/AffectedStopsFields";
import { TrainContext } from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/subTypeInputs/utils/formContextWrappers";
import {
  EitherTrainFormSchema,
  TrainMissingProductsEnum,
} from "features/CenterContent/VehicleDetails/TrainDetails/TrainCondition/OperationalTrainInfo/TrainInfoModal/types/formSchema";
import { TFunction } from "i18next";
import { FC, ReactElement, useEffect } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { ChoiceChip, Field, Stack } from "shared/components/Spor";
import { RenderErrorInPath } from "shared/components/forms/RenderErrorInPath";
import { Select } from "shared/components/forms/Select";
import { useCountryCodeForDropsRole } from "shared/hooks/useCountryCodeForDropsRole";
import {
  CountryCode,
  countryCode as countryCodeEnum,
} from "shared/types/countryCode";

type ProductType = {
  type: TrainMissingProductsEnum;
  name: string;
  outlineIcon: ReactElement;
  fillIcon: ReactElement;
  countryCodes: CountryCode[];
};

const getProducts = (
  translate: TFunction<"translation", undefined>,
): ProductType[] => [
  {
    type: "FIRST_CLASS_CAR",
    name: translate("Pluss"),
    outlineIcon: <PlussSeatOutline24Icon />,
    fillIcon: <PlussSeatFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "SLEEPING_CAR",
    name: translate("Sovevogn"),
    outlineIcon: <SleepOutline24Icon />,
    fillIcon: <SleepFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "FAMILY_CAR",
    name: translate("Familievogn"),
    outlineIcon: <FamilyOutline24Icon />,
    fillIcon: <FamilyFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "ANIMALS_ALLOWED_CAR",
    name: translate("Dyr tillatt vogn"),
    outlineIcon: <PetAllowedOutline24Icon />,
    fillIcon: <PetAllowedFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "ANIMALS_NOT_ALLOWED_CAR",
    name: translate("Dyrefri vogn"),
    outlineIcon: <PetNotAllowedOutline24Icon />,
    fillIcon: <PetNotAllowedFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "RESTAURANT_CAR",
    name: translate("Kafévogn"),
    outlineIcon: <CafeOutline24Icon />,
    fillIcon: <CafeFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "WIFI",
    name: translate("Trådløst internett"),
    outlineIcon: <WifiOutline24Icon />,
    fillIcon: <WifiFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "QUIET_SECTION",
    name: translate("Stille"),
    outlineIcon: <SoundOffOutline24Icon />,
    fillIcon: <SoundOffFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "FUNCTIONING_TOILETS",
    name: translate("Fungerende toaletter"),
    outlineIcon: <ToiletOutline24Icon />,
    fillIcon: <ToiletFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "COMPARTMENT",
    name: translate("Kupé"),
    outlineIcon: <SofaOutline24Icon />,
    fillIcon: <SofaFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "REST",
    name: translate("Hvile"),
    outlineIcon: <Sleep6BedsOutline24Icon />,
    fillIcon: <Sleep6BedsFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "PLUS_NIGHT",
    name: translate("PlussNatt"),
    outlineIcon: <RecliningSeatOutline24Icon />,
    fillIcon: <RecliningSeatFill24Icon />,
    countryCodes: [countryCodeEnum.enum.NO],
  },
  {
    type: "KIOSK",
    name: translate("Kiosk"),
    outlineIcon: <CafeOutline24Icon />,
    fillIcon: <CafeFill24Icon />,
    countryCodes: [countryCodeEnum.enum.SE],
  },
  {
    type: "FULL_KIOSK",
    name: translate("Komplett kiosk"),
    outlineIcon: <CafeOutline24Icon />,
    fillIcon: <CafeFill24Icon />,
    countryCodes: [countryCodeEnum.enum.SE],
  },
  {
    type: "BICYCLE_SPACE",
    name: translate("Sykkelplass"),
    outlineIcon: <BicycleOutline24Icon />,
    fillIcon: <BicycleFill24Icon />,
    countryCodes: [countryCodeEnum.enum.SE],
  },
];

export const TrainMissingProduct: FC<
  CommonTrainInfoFormProps & CommonSubTypeProps & TrainContext
> = ({ trainFormPrefix, ...props }) => {
  const { t } = useTranslation();
  const { getValues, setValue, register, formState } =
    useFormContext<EitherTrainFormSchema>();
  const { errors } = formState;
  const countryCode = useCountryCodeForDropsRole();

  const productsFieldPath = `${trainFormPrefix}.products` as const;
  const coverageFieldPath = `${trainFormPrefix}.coverage` as const;

  /**
   * Set 'productsFieldPath' to an empty array (from undefined)
   * so that the correct error is shown during triggers
   */
  useEffect(() => {
    const products = getValues(productsFieldPath);
    if (!products) setValue(productsFieldPath, []);
  }, []);

  const productIsChecked = (product: TrainMissingProductsEnum): boolean =>
    getValues(productsFieldPath)?.some((v) => v === product) ?? false;

  const onProductClick = (product: TrainMissingProductsEnum, evt: boolean) => {
    const oldValues = getValues(productsFieldPath) ?? [];
    if (evt) {
      if (!productIsChecked(product)) {
        setValue(productsFieldPath, [...oldValues, product], {
          shouldValidate: true,
        });
      }
    } else {
      setValue(
        productsFieldPath,
        oldValues.filter((v) => v !== product),
        { shouldValidate: true },
      );
    }
  };

  return (
    <>
      <AffectedStopsFields trainFormPrefix={trainFormPrefix} {...props} />
      <div>
        <Field marginBottom="12px">{t("Produkter som mangler:")}</Field>
        <Stack flexDirection="row" flexWrap="wrap">
          {getProducts(t)
            .filter((product) => product.countryCodes.includes(countryCode))
            .map((product) => (
              <ChoiceChip
                key={product.type}
                icon={{
                  default: product.outlineIcon,
                  checked: product.fillIcon,
                }}
                onCheckedChange={(evt) => onProductClick(product.type, evt)}
                checked={productIsChecked(product.type)}
              >
                {product.name}
              </ChoiceChip>
            ))}
        </Stack>
      </div>
      <RenderErrorInPath errors={errors} errorPath={productsFieldPath} />
      <Select
        {...register(coverageFieldPath)}
        value={getValues(coverageFieldPath)}
        label={`${t("Dekning")}*`}
        placeholder={t("Velg hvor produktene mangler")}
      >
        <option value="WHOLE_TRAIN">{t("Hele toget")}</option>
        <option value="FRONT_SET">{t("Fremre togsett")}</option>
        <option value="REAR_SET">{t("Bakre togsett")}</option>
      </Select>
      <RenderErrorInPath errors={errors} errorPath={coverageFieldPath} />
    </>
  );
};
